/**
 * Validate if a file is a PDF
 */
export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')
}

/**
 * Validate file size (in MB)
 */
export function isValidFileSize(file: File, maxSizeMB: number = 50): boolean {
  const fileSizeMB = file.size / (1024 * 1024)
  return fileSizeMB <= maxSizeMB
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

/**
 * Validate page range format
 * Examples: "1-5", "8", "1-3,5,7-9"
 */
export function validatePageRanges(ranges: string): boolean {
  if (!ranges.trim()) return false

  const parts = ranges.split(',').map(s => s.trim()).filter(s => s)

  for (const part of parts) {
    if (!/^(\d+)(-\d+)?$/.test(part)) {
      return false
    }

    // Validate that end >= start if it's a range
    if (part.includes('-')) {
      const [start, end] = part.split('-').map(Number)
      if (end < start) return false
    }
  }

  return true
}

/**
 * Parse page ranges into array of page numbers
 * Example: "1-3,5" => [1, 2, 3, 5]
 */
export function parsePageRanges(ranges: string, maxPage: number): number[] {
  const pages: number[] = []
  const parts = ranges.split(',').map(s => s.trim())

  for (const part of parts) {
    if (part.includes('-')) {
      const [start, end] = part.split('-').map(Number)
      for (let i = start; i <= end; i++) {
        if (i >= 1 && i <= maxPage) {
          pages.push(i)
        }
      }
    } else {
      const page = Number(part)
      if (page >= 1 && page <= maxPage) {
        pages.push(page)
      }
    }
  }

  // Remove duplicates and sort
  return [...new Set(pages)].sort((a, b) => a - b)
}

/**
 * Validate that all pages in range are within document bounds
 */
export function validatePageBounds(pages: number[], maxPage: number): boolean {
  return pages.every(page => page >= 1 && page <= maxPage)
}
